import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Input, InputRef, Space } from 'antd';

type VerificationCodeInputProps = {
  length?: number;
  disabled?: boolean;
  hasError?: boolean;
  onChange?: (code: string) => void;
  onComplete: (code: string) => void;
};

// One box per digit ([ ] [ ] [ ] [ ]), digits only. Typing moves on to the next box, Backspace on
// an empty box goes back, and pasting the whole code into any box spreads it over the following
// ones. onComplete fires as soon as every box is filled. A rejected code (hasError) is cleared so the
// next one is typed from the first box - otherwise changing a single digit of the old code would
// complete it again and submit a half-edited code.
const VerificationCodeInput = ({ length = 4, disabled, hasError, onChange, onComplete }: VerificationCodeInputProps) => {
  const [ digits, setDigits ] = useState<string[]>(Array(length).fill(''));
  const refs = useRef<(InputRef | null)[]>([]);

  const focus = (index: number) => refs.current[Math.max(0, Math.min(index, length - 1))]?.focus();

  // Rejected code: start over from the first box. The boxes keep their error mark until a digit is
  // typed (the parent lifts the error on the first non-empty change).
  useEffect(() => {
    if (!hasError) return;
    setDigits(Array(length).fill(''));
    onChange?.('');
    focus(0);
  }, [ hasError ]);

  const update = (next: string[]) => {
    setDigits(next);
    const code = next.join('');
    onChange?.(code);
    if (code.length === length) onComplete(code);
  };

  const handleChange = (index: number, raw: string) => {
    let value = raw.replace(/\D/g, '');
    // A box already holding a digit reports "old + new" when its content was not selected.
    if (value.length > 1 && digits[index] && value.startsWith(digits[index])) value = value.slice(1);

    const next = [ ...digits ];

    if (!value) {
      next[index] = '';
      update(next);
      return;
    }

    value.split('').slice(0, length - index).forEach((digit, offset) => { next[index + offset] = digit; });
    update(next);
    focus(index + value.length);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index]) focus(index - 1);
    if (event.key === 'ArrowLeft') focus(index - 1);
    if (event.key === 'ArrowRight') focus(index + 1);
  };

  return (
    <Space size="middle" className="verification-code-input">
      {digits.map((digit, index) => (
        <Input
          key={index}
          ref={(element) => { refs.current[index] = element; }}
          className="verification-code-digit"
          value={digit}
          size="large"
          maxLength={length}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          autoFocus={index === 0}
          disabled={disabled}
          status={hasError ? 'error' : undefined}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </Space>
  );
};

export default VerificationCodeInput;
